import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Typography from '@mui/material/Typography';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import Stepper from '@mui/material/Stepper';
import StepLabel from '@mui/material/StepLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

import grey from '@mui/material/colors/grey';

import {
  COMMERCIAL_PURPOSE_OPTIONS,
  EXTRACTION_RATIO_OPTIONS,
  CO2_INTERVENTION_TYPE,
  STEPS_IDS,
  FELLING_PROCESSING_TYPE,
  CO2_INTERVENTION_TYPE_OPTIONS,
  FELLING_PROCESSING_OPTIONS
} from '../../../../../config';

import EditableData from '../EditableData';
import DialogPanel from '../../../../DialogPanel';

import {getLabelById} from '../../../../../utils/getLabelById';
import {useTranslation} from 'react-i18next';

import SelectInput from '@geomatico/geocomponents/SelectInput';
import {getCurrentYear} from '../../../../../utils/getCurrentYear';
import {co2_intervention} from '../../../../proptypes/co2_intervention';

const Co2_InterventionDialog = ({
  minYear,
  maxYear,
  allCo2Interventions,
  plantingYear,
  isCo2InterventionDialogOpen,
  onDialogClose,
  onDialogOpen,
  onPlantingYearChange,
  onCo2InterventionAdd
}) => {

  if(!isCo2InterventionDialogOpen){
    return <></>;
  }

  const {t} = useTranslation();
  
  const hasInterventionOfTheType = (type) => allCo2Interventions
    .some(intervention => intervention.co2InterventionType === type);
  
  const lastFinalCuttingYear = useMemo(() => hasInterventionOfTheType(CO2_INTERVENTION_TYPE.FINAL_CUTTINGS)
    ? allCo2Interventions
      .sort((a, b) => b.year - a.year)
      .find(intervention => intervention.co2InterventionType === CO2_INTERVENTION_TYPE.FINAL_CUTTINGS).year
    : 9999, [allCo2Interventions]);

  const initialCo2Intervention = {
    year: Math.min(maxYear, getCurrentYear()),
    number_per_ha: 1,
    extraction_ratio: 0.75,
    extraction_tree_proportion: 0.75,
    residue: 0.50,
    extracted_volume: 0,
    commercial_purpose: COMMERCIAL_PURPOSE_OPTIONS[0].id,
    felling_type: FELLING_PROCESSING_TYPE.MECHANICAL,
    processing_type: FELLING_PROCESSING_TYPE.MECHANICAL,
    transport_distance: null,
    amount: 0,
    plants_per_ha: 1,
    nitrogen_ratio: 0,
    age: new Date().getFullYear() - plantingYear+1
  };//TODO: Generar una entidad por tipo de intervención y que setee el objeto newIntervention

  const plantingOption = useMemo(() => {
    return [
      {
        id: CO2_INTERVENTION_TYPE.PLANTINGS,
        label: getLabelById(CO2_INTERVENTION_TYPE.PLANTINGS, CO2_INTERVENTION_TYPE_OPTIONS)
      }
    ];
  },[]);

  const regenerationOption = useMemo(() => {
    return [
      {
        id: CO2_INTERVENTION_TYPE.REGENERATIONS,
        label: getLabelById(CO2_INTERVENTION_TYPE.REGENERATIONS, CO2_INTERVENTION_TYPE_OPTIONS)
      }
    ];
  },[]);

  const cuttingOptions = useMemo(() => {
    return [
      {
        id: CO2_INTERVENTION_TYPE.INTERMEDIATE_CUTTINGS,
        label: getLabelById(CO2_INTERVENTION_TYPE.INTERMEDIATE_CUTTINGS, CO2_INTERVENTION_TYPE_OPTIONS)
      },
      {
        id: CO2_INTERVENTION_TYPE.FINAL_CUTTINGS,
        label: getLabelById(CO2_INTERVENTION_TYPE.FINAL_CUTTINGS, CO2_INTERVENTION_TYPE_OPTIONS)
      }
    ];
  },[]);

  const clearingOption = useMemo(() => {
    return [
      {
        id: CO2_INTERVENTION_TYPE.CLEARINGS,
        label: getLabelById(CO2_INTERVENTION_TYPE.CLEARINGS, CO2_INTERVENTION_TYPE_OPTIONS)
      }
    ];
  },[]);

  const fertilizationsOption = useMemo(() => {
    return [
      {
        id: CO2_INTERVENTION_TYPE.FERTILIZATIONS,
        label: getLabelById(CO2_INTERVENTION_TYPE.FERTILIZATIONS, CO2_INTERVENTION_TYPE_OPTIONS)
      }
    ];
  },[]);

  const [activeStep, setActiveStep] = useState(0);
  const [newCo2Intervention, setNewCo2Intervention] = useState(initialCo2Intervention);
  const [isLastFinalCutting, setIsLastFinalCutting] = useState(false);

  //RESET NEW CO2 INTERVENTION TYPE
  useEffect(() => {
    activeStep === 0 && setNewCo2InterventionType(initialCo2InterventionType);
  }, [activeStep]);

  //DEFINE LA OPCIÓN SELECCIONADA POR DEFECTO EN EL SELECT DEL TIPO DE ACTUACIÓN
  const initialCo2InterventionType = useMemo(() =>
    isLastFinalCutting
      ? [...plantingOption, ...regenerationOption][0].id
      : cuttingOptions[0].id,
  [isLastFinalCutting, allCo2Interventions]);
  
  //DEFINE LAS OPCIONES DEL SELECT DEL TIPO DE ACTUACIÓN
  const interventionTypeOptions = useMemo(() => {

    if(isLastFinalCutting) {//Si la última es corta final solo deja Plantación y Regeneración
      return [...plantingOption, ...regenerationOption];
    }

    if(hasInterventionOfTheType(CO2_INTERVENTION_TYPE.REGENERATIONS)) { //Si tiene Regeneración, permite clareos
      return [...cuttingOptions, ...clearingOption, ...fertilizationsOption];
    }

    return [...cuttingOptions, ...fertilizationsOption];//Resto de casos permite Clara, CortaFinal y Fertilización
    
  },[isLastFinalCutting, allCo2Interventions]
  );

  const [newCo2InterventionType, setNewCo2InterventionType] = useState(initialCo2InterventionType);
  
  const FORMATTED_EXTRACTION_RATIO = EXTRACTION_RATIO_OPTIONS.map(e => ({
    id: e.id.toString(),
    label: e.label
  }));

  const handleBack = () => setActiveStep(activeStep - 1);
  const handleNext = () => setActiveStep(activeStep + 1);

  const handleCloseDialog = () => {
    onDialogClose();
    setActiveStep(0);
    setNewCo2Intervention(initialCo2Intervention);
  };

  const handleAddPlanting = () => {
    setActiveStep(0);
    setNewCo2Intervention(initialCo2Intervention);
  };
  
  const handleAddCo2Intervention = () => {

    switch (newCo2InterventionType) {

    case CO2_INTERVENTION_TYPE.INTERMEDIATE_CUTTINGS:
      onCo2InterventionAdd({
        co2InterventionType: newCo2InterventionType,
        year: newCo2Intervention.year,
        residue: newCo2Intervention.residue,
        extraction_ratio: newCo2Intervention.extraction_ratio,
        extraction_tree_proportion: newCo2Intervention.extraction_tree_proportion,
        extracted_volume: newCo2Intervention.extracted_volume,
        commercial_purpose: newCo2Intervention.commercial_purpose,
        felling_type: newCo2Intervention.felling_type,
        processing_type: newCo2Intervention.processing_type,
        transport_distance: newCo2Intervention.transport_distance,
        age: newCo2Intervention.year - plantingYear+1
      });
      handleCloseDialog();
      break;

    case CO2_INTERVENTION_TYPE.FINAL_CUTTINGS:
      onCo2InterventionAdd({
        co2InterventionType: newCo2InterventionType,
        year: newCo2Intervention.year,
        residue: newCo2Intervention.residue,
        extracted_volume: newCo2Intervention.extracted_volume,
        commercial_purpose: newCo2Intervention.commercial_purpose,
        felling_type: newCo2Intervention.felling_type,
        processing_type: newCo2Intervention.processing_type,
        transport_distance: newCo2Intervention.transport_distance,
        age: newCo2Intervention.year - plantingYear+1
      });
      handleAddPlanting();
      setIsLastFinalCutting(true);
      break;

    case CO2_INTERVENTION_TYPE.PLANTINGS:
      onCo2InterventionAdd({
        co2InterventionType: newCo2InterventionType,
        number_per_ha: newCo2Intervention.number_per_ha,
        year: lastFinalCuttingYear
      });
      setNewCo2InterventionType(CO2_INTERVENTION_TYPE.PLANTINGS);
      onPlantingYearChange(lastFinalCuttingYear);
      handleCloseDialog();
      setIsLastFinalCutting(false);
      break;
      
    case CO2_INTERVENTION_TYPE.REGENERATIONS:
      onCo2InterventionAdd({
        co2InterventionType: newCo2InterventionType,
        number_per_ha: newCo2Intervention.number_per_ha,
        year: lastFinalCuttingYear
      });
      setNewCo2InterventionType(CO2_INTERVENTION_TYPE.REGENERATIONS);
      onPlantingYearChange(lastFinalCuttingYear);
      handleCloseDialog();
      setIsLastFinalCutting(false);
      break;
      
    case CO2_INTERVENTION_TYPE.CLEARINGS:
      onCo2InterventionAdd({
        co2InterventionType: newCo2InterventionType,
        year: newCo2Intervention.year,
        age: newCo2Intervention.year - plantingYear+1
      });
      setNewCo2InterventionType(CO2_INTERVENTION_TYPE.CLEARINGS);
      handleCloseDialog();
      setIsLastFinalCutting(false);
      break;

    case CO2_INTERVENTION_TYPE.FERTILIZATIONS:
      onCo2InterventionAdd({
        co2InterventionType: newCo2InterventionType,
        age: newCo2Intervention.year - plantingYear+1,
        year: newCo2Intervention.year,
        amount: newCo2Intervention.amount,
        plants_per_ha: newCo2Intervention.plants_per_ha,
        nitrogen_ratio: newCo2Intervention.nitrogen_ratio,
      });
      setNewCo2InterventionType(CO2_INTERVENTION_TYPE.CLEARINGS);
      handleCloseDialog();
      setIsLastFinalCutting(false);
      break;

    default:
      console.warn('Tipo de intervención no reconocido:', newCo2InterventionType);
        
      if (newCo2InterventionType === CO2_INTERVENTION_TYPE.FINAL_CUTTINGS) {
        onDialogOpen();
      }
    }
  };

  const handlePropertyChange = (property) => setNewCo2Intervention({...newCo2Intervention, ...property});

  const allSteps = {
    [STEPS_IDS.CO2_INTERVENTION_TYPE]: {
      sublabel: t('Actuación'),
      sublabelWithSelectedOption: `${t('Actuación')}: ${getLabelById(newCo2InterventionType, CO2_INTERVENTION_TYPE_OPTIONS)}`,
      content: <Box my={1}>
        <SelectInput
          options={interventionTypeOptions}
          onOptionChange={setNewCo2InterventionType}
          selectedOptionId={newCo2InterventionType}
          menuSx={{zIndex: 3000}}
        />
      </Box>
    },
    [STEPS_IDS.YEAR]: {
      sublabel: t('Año de la actuación'),
      sublabelWithSelectedOption: `${t('Año')}: ${newCo2Intervention.year}`,
      content: <Box my={1}>
        <EditableData
          onChange={(value) => handlePropertyChange({year: value})}
          value={newCo2Intervention.year}
          max={Math.min(maxYear, getCurrentYear())}
          min={minYear}
        />
        <Typography variant='body2' component='span' sx={{mt: 1, color: grey[700]}}>
          {`Min ${minYear} - Máx ${Math.min(maxYear, getCurrentYear())}`}
        </Typography>
      </Box>
    },
    [STEPS_IDS.NUMBER_PER_HA]: {
      sublabel: t('Número de árboles por hectárea (N)'),
      sublabelWithSelectedOption: `${t('Número (N')}: ${newCo2Intervention.number_per_ha || initialCo2Intervention.number_per_ha}`,
      content: <Box>
        {
          newCo2InterventionType === CO2_INTERVENTION_TYPE.REGENERATIONS &&
          <Typography sx={{mb: 1, color: grey[700], fontStyle: 'italic'}}>
            {t('Indica el Nº de pies que se dejarán cuando se realice el clareo.')}
          </Typography>
        }
        <EditableData
          onChange={(value) => handlePropertyChange({number_per_ha: value})}
          value={newCo2Intervention.number_per_ha}
          min={1}
        />
      </Box>
    },
    [STEPS_IDS.EXTRACTION_RATIO]: {
      sublabel: t('Relación de extracción de la clara (R)'),
      sublabelWithSelectedOption: `${t('Extracción (R)')}: ${getLabelById(newCo2Intervention.extraction_ratio, EXTRACTION_RATIO_OPTIONS)}`,
      content: <SelectInput
        options={FORMATTED_EXTRACTION_RATIO}
        selectedOptionId={newCo2Intervention.extraction_ratio?.toString() || EXTRACTION_RATIO_OPTIONS[0].id.toString()}
        onOptionChange={(value) => handlePropertyChange({extraction_ratio: Number(value)})}
        menuSx={{zIndex: 3000}}
      />
    },
    [STEPS_IDS.EXTRACTION_TREE_PROPORTION]: {
      sublabel: t('Proporción de árboles a extraer (i)'),
      sublabelWithSelectedOption: `${t('Proporción (i)')}: ${getLabelById(newCo2Intervention.extraction_ratio, EXTRACTION_RATIO_OPTIONS)}`,
      content: <EditableData
        onChange={(value) => handlePropertyChange({extraction_tree_proportion: Number(value)})}
        value={newCo2Intervention.extraction_tree_proportion}
        step={0.05}
        min={0.05}
        max={0.95}
      />
    },
    [STEPS_IDS.RESIDUE]: {
      sublabel: t('Restos del suelo (rs)'),
      sublabelWithSelectedOption: `${t('Restos (rs)')}: ${newCo2Intervention.residue || initialCo2Intervention.residue}`,
      content: <EditableData
        onChange={(value) => handlePropertyChange({residue: value})}
        value={newCo2Intervention.residue}
        step={0.05}
        min={0.05}
        max={0.95}
      />
    },
    [STEPS_IDS.EXTRACTED_VOLUME]: {
      sublabel: t('Volumen de madera extraída'),
      sublabelWithSelectedOption: `${t('Volumen (m3)')}: ${newCo2Intervention.extracted_volume === 0 
        ? t('POR DEFINIR' )
        : newCo2Intervention.extracted_volume}`,
      content: <Box my={1}>
        {
          newCo2Intervention.felling_type === FELLING_PROCESSING_TYPE.MECHANICAL &&
          <Typography sx={{mb: 1, color: grey[700], fontStyle: 'italic'}}>
            {t('Introduce el valor de la madera extraída tras pesar los camiones, si aún no conoces el valor podrás' +
              ' introducirlo más adelante, pero será necesario de cara al cálculo de emisiones.')}
          </Typography>
        }
        <EditableData
          onChange={(value) => handlePropertyChange({extracted_volume: value})}
          value={newCo2Intervention.extracted_volume}
          min={1}
        />
      </Box>
    },
    [STEPS_IDS.FELLING_TYPE]: {
      sublabel:  t('Apeo y procesado'),
      sublabelWithSelectedOption: `${t('APEO')}: ${getLabelById(newCo2Intervention.felling_type,FELLING_PROCESSING_OPTIONS)} - ${t('PROCESADO')}: ${getLabelById(newCo2Intervention.processing_type, FELLING_PROCESSING_OPTIONS)} `,
      content: <Box display='flex' flexDirection='column'>
        <Box my={1} sx={{display: 'flex', flexDirection: 'row', gap: 10}}>
          <RadioGroup value={newCo2Intervention.felling_type} onChange={(e) => handlePropertyChange({felling_type: e.target.value})}>
            <FormLabel>{t('Apeo')}</FormLabel>
            <FormControlLabel value={FELLING_PROCESSING_TYPE.MECHANICAL} control={<Radio/>}
              label={getLabelById(FELLING_PROCESSING_TYPE.MECHANICAL, FELLING_PROCESSING_OPTIONS)}/>
            <FormControlLabel value={FELLING_PROCESSING_TYPE.MANUAL} control={<Radio/>}
              label={getLabelById(FELLING_PROCESSING_TYPE.MANUAL, FELLING_PROCESSING_OPTIONS)}/>
          </RadioGroup>
          <RadioGroup
            value={newCo2Intervention.felling_type === FELLING_PROCESSING_TYPE.MECHANICAL ? FELLING_PROCESSING_TYPE.MECHANICAL : newCo2Intervention.processing_type}
            onChange={(e) => handlePropertyChange({processing_type: e.target.value})}
          >
            <FormLabel>{t('Procesado')}</FormLabel>
            <FormControlLabel value={FELLING_PROCESSING_TYPE.MECHANICAL} control={<Radio/>}
              label={getLabelById(FELLING_PROCESSING_TYPE.MECHANICAL, FELLING_PROCESSING_OPTIONS)}/>
            <FormControlLabel value={FELLING_PROCESSING_TYPE.MANUAL} control={<Radio disabled={newCo2Intervention.felling_type === FELLING_PROCESSING_TYPE.MECHANICAL}/>}
              label={getLabelById(FELLING_PROCESSING_TYPE.MANUAL, FELLING_PROCESSING_OPTIONS)}/>
          </RadioGroup>
        </Box>
        <Typography sx={{color: grey[700], fontStyle: 'italic'}}>
          {t('Si el Apeo es mecanizado el Procesado debe ser mecanizado también.')}
        </Typography>
      </Box>
    },
    [STEPS_IDS.COMMERCIAL_PURPOSE]: {
      sublabel: t('Producto objetivo'),
      sublabelWithSelectedOption: `${t('Producto')}: 
        ${COMMERCIAL_PURPOSE_OPTIONS.filter(purpose => purpose.id === newCo2Intervention.commercial_purpose)[0].label.toUpperCase()}`,
      content: <Box my={1}>
        <SelectInput
          options={COMMERCIAL_PURPOSE_OPTIONS}
          onOptionChange={(value) => handlePropertyChange({commercial_purpose: value})}
          selectedOptionId={newCo2Intervention.commercial_purpose}
          menuSx={{zIndex: 3000}}
        />
      </Box>
    },
    [STEPS_IDS.TRANSPORT_DISTANCE]: {
      sublabel: t('Distancia en km'),
      sublabelWithSelectedOption: `${t('Distancia')}: ${newCo2Intervention.transport_distance || t('Sin transporte')}`,
      content: <EditableData
        onChange={(value) => handlePropertyChange({ transport_distance: value })}
        value={newCo2Intervention.transport_distance}
        min={0}
      />
    },
    [STEPS_IDS.AMOUNT]: {
      sublabel: t('Cantidad (gr/planta)'),
      sublabelWithSelectedOption: `${t('Cantidad')}: ${newCo2Intervention.amount}`,
      content: <EditableData
        onChange={(value) => handlePropertyChange({amount: Number(value)})}
        value={newCo2Intervention.amount}
        step={1}
      />
    },
    [STEPS_IDS.PLANTS_PER_HA]: {
      sublabel: t('Plantas por hectárea (ud/ha)'),
      sublabelWithSelectedOption: `${t('Plantas')}: ${newCo2Intervention.plants_per_ha}`,
      content: <EditableData
        onChange={(value) => handlePropertyChange({plants_per_ha: Number(value)})}
        value={newCo2Intervention.plants_per_ha}
        step={1}
      />
    },
    [STEPS_IDS.NITROGEN_RATIO]: {
      sublabel: t('Porcentaje de nitrógeno del abono'),
      sublabelWithSelectedOption: `${t('%')}: ${newCo2Intervention.nitrogen_ratio}`,
      content: <Box my={1}>
        <EditableData
          onChange={(value) => handlePropertyChange({nitrogen_ratio: Number(value)})}
          value={newCo2Intervention.nitrogen_ratio}
          step={0.1}
          min={0}
          max={1}
        />
        <Typography variant='body2' component='span' sx={{mt: 1, color: grey[700]}}>Min 0 - Máx 1</Typography>
      </Box>
    }
  };

  //DEFINE LAS PREGUNTAS EN FUNCIÓN DEL TIPO DE INTERVENCIÓN
  const calculatedSteps = useMemo(() => {
    switch (newCo2InterventionType) {
    
    case CO2_INTERVENTION_TYPE.INTERMEDIATE_CUTTINGS:
      return [
        allSteps[STEPS_IDS.CO2_INTERVENTION_TYPE],
        allSteps[STEPS_IDS.YEAR],
        allSteps[STEPS_IDS.EXTRACTION_RATIO],
        allSteps[STEPS_IDS.EXTRACTION_TREE_PROPORTION],
        allSteps[STEPS_IDS.RESIDUE],
        allSteps[STEPS_IDS.TRANSPORT_DISTANCE],
        allSteps[STEPS_IDS.EXTRACTED_VOLUME],
        allSteps[STEPS_IDS.COMMERCIAL_PURPOSE],
      ];

    case CO2_INTERVENTION_TYPE.FINAL_CUTTINGS:
      return [
        allSteps[STEPS_IDS.CO2_INTERVENTION_TYPE],
        allSteps[STEPS_IDS.YEAR],
        allSteps[STEPS_IDS.RESIDUE],
        allSteps[STEPS_IDS.FELLING_TYPE],
        allSteps[STEPS_IDS.EXTRACTED_VOLUME],
        allSteps[STEPS_IDS.COMMERCIAL_PURPOSE],
        allSteps[STEPS_IDS.TRANSPORT_DISTANCE],
      ];
      
    case CO2_INTERVENTION_TYPE.PLANTINGS:
      return isLastFinalCutting
        ? [
          allSteps[STEPS_IDS.CO2_INTERVENTION_TYPE],
          allSteps[STEPS_IDS.NUMBER_PER_HA],
        ]
        : [
          allSteps[STEPS_IDS.CO2_INTERVENTION_TYPE],
          allSteps[STEPS_IDS.YEAR],
          allSteps[STEPS_IDS.NUMBER_PER_HA],
        ];

    case CO2_INTERVENTION_TYPE.REGENERATIONS:
      return isLastFinalCutting
        ? [
          allSteps[STEPS_IDS.CO2_INTERVENTION_TYPE],
          allSteps[STEPS_IDS.NUMBER_PER_HA],
        ]
        : [
          allSteps[STEPS_IDS.CO2_INTERVENTION_TYPE],
          allSteps[STEPS_IDS.YEAR],
          allSteps[STEPS_IDS.NUMBER_PER_HA],
        ];
    
    case CO2_INTERVENTION_TYPE.CLEARINGS:
      return [
        allSteps[STEPS_IDS.CO2_INTERVENTION_TYPE],
        allSteps[STEPS_IDS.YEAR]
      ];
      
    case CO2_INTERVENTION_TYPE.FERTILIZATIONS:
      return [
        allSteps[STEPS_IDS.CO2_INTERVENTION_TYPE],
        allSteps[STEPS_IDS.YEAR],
        allSteps[STEPS_IDS.AMOUNT],
        allSteps[STEPS_IDS.PLANTS_PER_HA],
        allSteps[STEPS_IDS.NITROGEN_RATIO]
      ];

    }
  }, [newCo2Intervention, newCo2InterventionType]);

  return <DialogPanel
    isOpen={isCo2InterventionDialogOpen}
    onClose={onDialogClose}
    label={t('AÑADIR ACTUACIÓN')}
    fullWidth
  >
    <Stepper activeStep={activeStep} orientation="vertical" sx={{ml: 2, mb: 2}}>
      {
        calculatedSteps.map(({sublabel, sublabelWithSelectedOption, content}, index) =>
          <Step key={index}>
            <StepLabel>{activeStep > index ? sublabelWithSelectedOption : sublabel}</StepLabel>
            <StepContent>
              {content}
              <Box mt={2}>
                <Button disabled={activeStep === 0} onClick={handleBack}>{t('Atrás')}</Button>
                <Button variant='contained' color='secondary'
                  onClick={
                    activeStep === calculatedSteps.length - 1
                      ? handleAddCo2Intervention
                      : handleNext
                  }
                >
                  {
                    activeStep === calculatedSteps.length - 1
                      ? t('guardar')
                      : t('siguiente')
                  }
                </Button>
              </Box>
            </StepContent>
          </Step>
        )
      }
    </Stepper>
    {
      newCo2InterventionType === CO2_INTERVENTION_TYPE.INTERMEDIATE_CUTTINGS && <Alert severity="info" sx={{mx: 1, mb: 2}}>
        <Typography sx={{fontStyle: 'italic'}}>
          {t('El apeo y el procesado se consideran mecanizados de cara al cálculo de emisiones.')}
        </Typography>
      </Alert>
    }
    {
      (newCo2InterventionType === CO2_INTERVENTION_TYPE.PLANTINGS || newCo2InterventionType === CO2_INTERVENTION_TYPE.REGENERATIONS) &&
      <Alert severity="info" sx={{mx: 1, mb: 2}}>
        <Typography sx={{fontStyle: 'italic'}}>
          {t('Esta actuación incluye las emisiones de la trituración de restos, del desbroce previo y la preparación' +
            ' del terreneo en el cálculo del consumo del CO')}<sub>2</sub>
        </Typography>
      </Alert>
    }
  </DialogPanel>;
};

Co2_InterventionDialog.propTypes = {
  minYear: PropTypes.number.isRequired,
  maxYear: PropTypes.number.isRequired,
  allCo2Interventions: PropTypes.arrayOf(co2_intervention),
  plantingYear: PropTypes.number.isRequired,
  isCo2InterventionDialogOpen: PropTypes.bool.isRequired,
  onDialogClose: PropTypes.func.isRequired,
  onDialogOpen: PropTypes.func.isRequired,
  onPlantingYearChange: PropTypes.func.isRequired,
  onCo2InterventionAdd: PropTypes.func.isRequired
};

export default Co2_InterventionDialog;